/**
 * StreamLain BlockSuite Hooks Registration
 *
 * Registers StreamLain extensions via window globals that BlockSuite checks.
 * This approach avoids direct modifications to BlockSuite files.
 */

import type { GanttWizardStrings } from '@blocksuite/affine/data-view';

/**
 * StreamLain hooks interface.
 * Note: The Window interface is declared in filesys.ts to avoid duplication.
 */
export interface StreamlainHooks {
  /**
   * Additional file types for openFilesWith() in filesys.ts
   */
  fileTypes?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
  /**
   * Confirm before deleting multiple rows.
   * Called from BlockSuite Lit layer, implemented by React layer.
   * Returns a Promise that resolves to true (confirmed) or false (cancelled).
   */
  confirmDeleteRows?: (count: number) => Promise<boolean>;
  /**
   * Translated copy for the Gantt setup wizard. Consumed by the Lit
   * wizard component (`dv-gantt-setup-wizard`) via the tenant hook so
   * the data-view package stays free of frontend-i18n dependencies.
   * Any missing key falls back to the English default.
   */
  ganttWizardStrings?: Partial<GanttWizardStrings>;
}

/**
 * Register StreamLain hooks before BlockSuite initializes.
 * Must be called early in app startup.
 */
export function registerStreamlainHooks(hooks: StreamlainHooks): void {
  if (typeof window === 'undefined') return;

  // Cast to extended window type
  const win = window as Window & { __streamlainHooks?: StreamlainHooks };

  // Merge with existing hooks (allows multiple registrations)
  win.__streamlainHooks = {
    ...win.__streamlainHooks,
    ...hooks,
    // fileTypes are additive — concatenate instead of replacing
    fileTypes: [
      ...(win.__streamlainHooks?.fileTypes ?? []),
      ...(hooks.fileTypes ?? []),
    ],
  };
}

/**
 * Get registered StreamLain hooks.
 * Used by BlockSuite integration points.
 */
export function getStreamlainHooks(): StreamlainHooks {
  const win = window as Window & { __streamlainHooks?: StreamlainHooks };
  return win.__streamlainHooks ?? {};
}
